import { memo, useEffect, useRef, useState } from 'react';
import { DownArrowIcon } from '../Icons/DownArrowIcon';

export interface SelectOption {
  label: string;
  value: string;
  badge?: string;
}

export interface CustomSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: 'small' | 'large';
  disabled?: boolean;
}

export const CustomSelect = memo(
  ({
    options,
    value,
    onChange,
    placeholder = 'Select...',
    className = '',
    size = 'large',
    disabled = false,
  }: CustomSelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (optionValue: string) => {
      onChange(optionValue);
      setIsOpen(false);
    };

    const selectedOption = options.find((opt) => opt.value === value);
    const displayLabel = selectedOption?.label || placeholder;

    const buttonHeight = size === 'small' ? 'h-[32px]' : 'h-[40px]';
    const textSize = size === 'small' ? 'text-xs' : 'text-sm';
    const padding = size === 'small' ? 'px-3 py-1' : 'px-4 py-2';

    return (
      <div ref={dropdownRef} className={`relative inline-block ${className}`}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className={`tc-mv-question-field flex items-center justify-between text-white/80 ${textSize} font-medium leading-[130%] ${buttonHeight} rounded-full ${padding} focus:outline-none transition-colors duration-200 w-full ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}>
          <span className="flex items-center gap-2 truncate">
            <span className="truncate">{displayLabel}</span>
            {selectedOption?.badge && (
              <span className="text-[10px] text-white/60 bg-white/10 px-1.5 py-0.5 rounded">
                {selectedOption.badge}
              </span>
            )}
          </span>
          <span
            className={`flex items-center justify-center transition-transform duration-200 origin-center ${
              isOpen ? 'rotate-180' : ''
            }`}>
            <DownArrowIcon />
          </span>
        </button>

        {isOpen && !disabled && (
          <div className="absolute z-[99999] w-full mt-1 bg-black border border-white/20 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`w-full px-3 py-2 text-xs text-left hover:bg-white/10 transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg flex items-center justify-between gap-2 ${
                  option.value === value
                    ? 'bg-white/20 text-white font-medium'
                    : 'text-white/80'
                }`}>
                <span className="truncate">{option.label}</span>
                {option.badge && (
                  <span className="text-[10px] text-white/60 bg-white/10 px-1.5 py-0.5 rounded">
                    {option.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);

CustomSelect.displayName = 'CustomSelect';
